import {
  AMCAdditionalFieldValues,
  REPORT_FREQUENCY,
} from "~/typedef/amc/scheduledReport";
import {
  AMCReportTypeSelect,
  useCreateScheduledReportMutation,
  useGetReportTypesQuery,
} from "~/store/mystore/amc.redux";
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  COMPARISON_PERIOD,
  DATETIME_PERIODS,
  DATETIME_PERIODS_KEY,
  getDatesFromPeriod,
} from "~/store/utils/dateTimeUtils";
import { GenericSelect, Option } from "~/components/select/genericSelect";
import React, { useCallback, useEffect, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { get, omit } from "lodash";
import {
  getDefaultParameterValues,
  isValidParameterValues,
} from "./additionalFields/utils";

import AMCIncludeCommentaryToggle from "./additionalFields/amcIncludeCommentaryToggle";
import AdditionalFieldsRenderer from "./additionalFields/additionalFieldsRenderer";
import AutoSizedButton from "~/components/buttons/autoSizedButton";
import ComparisonPeriodSelect from "~/components/select/comparisonPeriodSelect";
import CurrencyDropdown from "~/components/toolbars/toolbarComponents/currencyDropdown";
import { CurrentStore } from "~/typedef/store";
import { DateRange } from "~/typedef/date";
import DateRangeDropdown from "~/components/dateRangePicker/fullDateTimeDropdown";
import { ErrorMessage } from "~/pages/marketplaceConnectors/amazonUpdateStoreNamePage";
import InfoAlertV2 from "~/components/alert/infoAlertV2";
import Panel from "~/components/panel/panel";
import PanelLoading from "~/components/loadingIndicator/panelLoadingIndicator";
import { PrunedAMCReportType } from "~/typedef/amc/reportType";
import TimezoneSelect from "@components/toolbars/toolbarComponents/timezoneDropdown";
import { getScheduledDescription } from "@pages/reports/reportUtils";
import styled from "styled-components";
import { useDateRangeFilters } from "~/hooks/useDateRangeFilters";
import useQueryParams from "~/hooks/useQueryParams";
import { useTypedSelector } from "~/hooks/useTypedSelector";

// const PAGE_SIZE = 5;

const Form = styled.form`
  display: block;
  padding: 1rem;
  max-width: 800px;
`;

interface FrequencyOption {
  name: REPORT_FREQUENCY;
  label: string;
}

const FREQUENCY_OPTIONS: FrequencyOption[] = [
  { name: REPORT_FREQUENCY.ONCE, label: "createReport.sendOnce" },
  { name: REPORT_FREQUENCY.DAILY, label: "createReport.daily" },
  { name: REPORT_FREQUENCY.WEEKLY, label: "createReport.weekly" },
  { name: REPORT_FREQUENCY.MONTHLY, label: "createReport.monthly" },
];

interface ReportingDelayOption {
  name: number;
  label: string;
}

const getReportingDelayOptions = (
  t: TFunction<"translation", undefined>
): ReportingDelayOption[] => {
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => {
    switch (num) {
      case 0:
        return { name: num, label: t("createReport.noReportingDelay") };
      case 1:
        return { name: num, label: `${num} ${t("day")}` };
      default:
        return { name: num, label: `${num} ${t("days")}` };
    }
  });
};

const getPeriod = (period: DATETIME_PERIODS): DATETIME_PERIODS => {
  return (Object.values(DATETIME_PERIODS).find((value) => value === period) || [
    "",
  ]) as DATETIME_PERIODS;
};

const DEFAULT_DATE_OPTIONS = Object.keys(
  omit(DATETIME_PERIODS, "DAY")
) as DATETIME_PERIODS_KEY[];

const SCHEDULED_DATE_OPTIONS = Object.keys(
  omit(DATETIME_PERIODS, "DAY", "CUSTOM")
) as DATETIME_PERIODS_KEY[];

interface ReportConfigFormProps {
  store: CurrentStore;
}

export const AmcReportConfigForm: React.FC<ReportConfigFormProps> = ({
  store,
}) => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const selectedReportType = params.get("reportType");

  const user = useTypedSelector((state) => get(state, "user"));
  const { currentPeriod, selectedTimezone, currentCompare, currentRange } =
    useDateRangeFilters();
  const currentCurrency = useTypedSelector((state) =>
    get(state, "persistentAppSettings.setting.data.currentCurrency")
  );
  // const includeTaxDefault = useTypedSelector((state) =>
  //   Boolean(state.persistentAppSettings?.setting?.data?.includeTax)
  // );

  const [name, setName] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [errorForCustomPrompt, setErrorForCustomPrompt] = useState(false);
  const [queryType, setQueryType] = useState<PrunedAMCReportType | null>(null);
  // const [pageSize, setPageSize] = useState(PAGE_SIZE);
  // const [includeTax, setIncludeTax] = useState(includeTaxDefault);
  const [period, setPeriod] = useState(currentPeriod);
  const [comparisonPeriod, setComparisonPeriod] = useState(currentCompare);
  const [fromDate, setFromDate] = useState(currentRange.fromDate); // default to current range
  const [toDate, setToDate] = useState(currentRange.toDate);
  const [priorFromDate, setPriorFromDate] = useState(
    currentRange.priorFromDate
  ); // default to current range
  const [priorToDate, setPriorToDate] = useState(currentRange.priorToDate);
  const [interval, setInterval] = useState(currentRange.interval);
  const [currency, setCurrency] = useState<string>(currentCurrency ?? "USD");
  const [emailsToBeSent, setEmailsToBeSent] = useState<string[]>([]);
  const [parameterValues, setParameterValues] =
    useState<AMCAdditionalFieldValues>({});

  const [timezone, setTimezone] = useState(selectedTimezone);
  const [frequency, setFrequency] = useState<REPORT_FREQUENCY>(
    REPORT_FREQUENCY.ONCE
  ); // once off
  const [reportingDelay, setReportingDelay] = useState<number | undefined>(0); // in days
  const [dateOptions, setDateOptions] = useState(DEFAULT_DATE_OPTIONS);
  const [error, setError] = useState(false);
  // const [emailError, setEmailError] = useState(false);
  const [includeCommentary, setIncludeCommentary] = useState(true);
  // mid exists in report type only for custom reports
  const isCustomReport = queryType?.mid;

  const { amcReportTypes, reportTypesLoading } = useGetReportTypesQuery(
    {
      activeOnly: true,
      filterType: AMCReportTypeSelect.ALL,
    },
    {
      selectFromResult: ({ data, isFetching }) => {
        return {
          amcReportTypes: data?.reportTypes ?? [],
          reportTypesLoading: isFetching,
        };
      },
    }
  );

  useEffect(() => {
    if (selectedReportType) {
      const reportType = amcReportTypes.find(
        (item) => item._id === selectedReportType
      );
      if (reportType) {
        setQueryType(reportType);
      } else {
        setQueryType(amcReportTypes[0]);
      }
    } else {
      setQueryType(amcReportTypes[0]);
    }
  }, [amcReportTypes]);

  const setNonStoreStateDefaults = () => {
    setName("");
    setPeriod(currentPeriod);
    setComparisonPeriod(currentCompare);
    setFromDate(currentRange.fromDate); // default to current range
    setToDate(currentRange.toDate);
    setPriorFromDate(currentRange.priorFromDate);
    setPriorToDate(currentRange.priorToDate);
    setInterval(currentRange.interval);
    setTimezone(selectedTimezone);
    if (currentCurrency) {
      setCurrency(currentCurrency);
    }
    setFrequency(REPORT_FREQUENCY.ONCE); // once off
    setReportingDelay(undefined);
    setParameterValues({});
    setIncludeCommentary(true);
    setCustomPrompt("");
    setErrorForCustomPrompt(false);
  };

  useEffect(() => {
    setNonStoreStateDefaults();
  }, []);

  useEffect(() => {
    if (user?.email) {
      setEmailsToBeSent([user.email]);
    }
  }, []);

  useEffect(() => {
    if (queryType) {
      setParameterValues(getDefaultParameterValues(queryType));
      if (customPrompt.length === 0) {
        setCustomPrompt(
          queryType && queryType.commentaryPrompt
            ? queryType.commentaryPrompt
            : ""
        );
      }
    }
  }, [queryType]);

  const handleAdditionalFieldsChange = (
    additionalFieldValues: AMCAdditionalFieldValues
  ) => {
    // Each additional field returns a subset of the parameterValues object
    // We need to merge them together
    setParameterValues((prev) => {
      return {
        ...prev,
        ...additionalFieldValues,
      };
    });
  };

  const reportingDelayOptions = getReportingDelayOptions(t);
  const selectedReportingDelay = reportingDelayOptions.find(
    (option) => option.name === reportingDelay
  );

  const selectedFrequencyOption = FREQUENCY_OPTIONS.find(
    (option) => option.name === frequency
  );

  const handleFrequencySelect = (option: Option) => {
    const newFreq = option.name;
    setFrequency(newFreq as REPORT_FREQUENCY);
    if (newFreq === REPORT_FREQUENCY.ONCE) {
      // Send Once - no delay
      setReportingDelay(undefined);
    }
  };

  useEffect(() => {
    if (frequency === REPORT_FREQUENCY.ONCE) {
      setDateOptions(DEFAULT_DATE_OPTIONS);
    } else {
      setDateOptions(SCHEDULED_DATE_OPTIONS);
    }
  }, [frequency]);

  useEffect(() => {
    if (frequency !== REPORT_FREQUENCY.ONCE) {
      const {
        fromDate: convertedFromDate,
        toDate: convertedToDate,
        priorFromDate: convertedPriorFromDate,
        priorToDate: convertedPriorToDate,
      } = getDatesFromPeriod(period, comparisonPeriod, timezone);
      setFromDate(convertedFromDate);
      setToDate(convertedToDate);
      setPriorFromDate(convertedPriorFromDate);
      setPriorToDate(convertedPriorToDate);
    }
  }, [timezone]);

  const [createScheduledReport, { isLoading }] =
    useCreateScheduledReportMutation();

  const handleSave = () => {
    if (store && queryType) {
      if (!isValidParameterValues(queryType, parameterValues)) {
        setError(true);
        return;
      }

      const payload = {
        mid: store.merchantId,
        queryType: queryType._id,
        reportName: name,
        reportConfig: {
          frequency,
          currentPeriod: period,
          comparisonPeriod,
          timezone,
          currency,
          reportingDelay,
          fromDate,
          toDate,
          priorFromDate,
          priorToDate,
          interval,
          includeCommentary,
          customPrompt,
        },
        reportRecipients: emailsToBeSent,
        parameterValues,
        successMsg: t("amc.createScheduledReport.success"),
      };

      createScheduledReport(payload);

      // clear the form on save
      setNonStoreStateDefaults();
    }
  };

  /** for clearing the success message if the user
   * edits the form again */
  const clearSuccessOrErrorMessage = () => {
    setError(false);
  };

  const onSetComparisonPeriod = useCallback(
    (comparison: COMPARISON_PERIOD) => {
      const range = getDatesFromPeriod(period, comparison, timezone);
      setDates(range, period, comparison, timezone);
    },
    [period, timezone]
  );

  const setDates = (
    range: DateRange,
    period: DATETIME_PERIODS,
    comparison: COMPARISON_PERIOD,
    timezone: string
  ) => {
    setFromDate(range.fromDate);
    setToDate(range.toDate);
    setPriorFromDate(range.priorFromDate);
    setPriorToDate(range.priorToDate);
    setInterval(range.interval);
    setPeriod(period);
    setTimezone(timezone);
    setComparisonPeriod(comparison);
  };

  return (
    <>
      <Box mb={1}>
        <Grid
          container
          spacing={1}
          justifyContent="flex-start"
          alignItems="flex-end"
        >
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <GenericSelect
                id="report-query-select"
                title={t("createReport.reportType")}
                tooltip={
                  queryType
                    ? isCustomReport
                      ? queryType.description
                      : t(`amc.${queryType.name}.description`)
                    : undefined
                }
                currentOption={
                  queryType
                    ? {
                        name: queryType._id,
                        label: isCustomReport
                          ? queryType.name
                          : t(`amc.${queryType.name}.label`),
                      }
                    : undefined
                }
                setOption={(option) => {
                  const reportType = amcReportTypes.find(
                    (item) => item._id === option.name
                  );
                  reportType && setQueryType(reportType);
                }}
                options={amcReportTypes.map((reportType) => ({
                  name: reportType._id,
                  label: reportType.mid
                    ? reportType.name
                    : t(`amc.${reportType.name}.label`),
                }))}
                placeholderLabel={t("amc.selectReportType.label")}
              />
            </FormControl>
          </Grid>
          {queryType && (
            <Grid item xs={12} md={8}>
              <InfoAlertV2
                message={
                  isCustomReport
                    ? queryType.description ?? ""
                    : t(`amc.${queryType.name}.description`)
                }
                noBorder
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <Box>
        <Panel
          id="create-report"
          title={t("panel.createReport")}
          content={
            <Form
              method="POST"
              onSubmit={handleSave}
              onChange={clearSuccessOrErrorMessage}
            >
              <Grid container spacing={2}>
                {isLoading || reportTypesLoading || !store ? (
                  <Grid item xs={12}>
                    <PanelLoading />
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <TextField
                          fullWidth
                          label={t("createReport.formHeader")}
                          placeholder={t("createReport.formHeaderPlaceholder")}
                          required
                          value={name}
                          onChange={(e) => {
                            setName(e.target.value);
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <GenericSelect
                          id="report-frequency-select"
                          title={t("createReport.selectFrequencyTitle")}
                          currentOption={
                            selectedFrequencyOption
                              ? {
                                  name: selectedFrequencyOption.name,
                                  label: t(selectedFrequencyOption.label),
                                }
                              : undefined
                          }
                          setOption={handleFrequencySelect}
                          options={FREQUENCY_OPTIONS.map((option) => ({
                            name: option.name,
                            label: t(option.label),
                          }))}
                          placeholderLabel={t(
                            "createReport.selectFrequencyTitle"
                          )}
                        />
                      </FormControl>
                    </Grid>
                    {frequency === REPORT_FREQUENCY.ONCE ? (
                      <Grid item xs={12} md={8}>
                        <FormControl fullWidth>
                          <DateRangeDropdown
                            title={t("createReport.selectDateRangeTitle")}
                            setDates={setDates}
                            period={getPeriod(period)}
                            range={{
                              fromDate,
                              toDate,
                              priorFromDate,
                              priorToDate,
                              timezone,
                              interval,
                            }}
                            compare={comparisonPeriod}
                            timezone={timezone}
                            options={dateOptions}
                            rightAlign={true}
                            useList={false}
                            fullWidth
                          />
                        </FormControl>
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <DateRangeDropdown
                              title={t("createReport.selectDateRangeTitle")}
                              setDates={setDates}
                              period={getPeriod(period)}
                              range={{
                                fromDate,
                                toDate,
                                priorFromDate,
                                priorToDate,
                                timezone,
                                interval,
                              }}
                              compare={comparisonPeriod}
                              timezone={timezone}
                              options={dateOptions}
                              rightAlign={true}
                              useList={true}
                              minWidth={100}
                              fullWidth
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <ComparisonPeriodSelect
                              fullWidth
                              setCompare={onSetComparisonPeriod}
                              currentCompare={comparisonPeriod}
                            />
                          </FormControl>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <CurrencyDropdown
                          currentCurrency={currency}
                          switchCurrency={setCurrency}
                          fullWidth
                        />
                      </FormControl>
                    </Grid>
                    {frequency !== REPORT_FREQUENCY.ONCE && (
                      <>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <GenericSelect
                              id="report-delay-select"
                              title={t(
                                "createReport.selectReportingDelayTitle"
                              )}
                              tooltip={t("reportingDelay.tooltip")}
                              currentOption={
                                selectedReportingDelay
                                  ? {
                                      name: selectedReportingDelay.name.toString(),
                                      label: t(selectedReportingDelay.label),
                                    }
                                  : undefined
                              }
                              setOption={(option) =>
                                setReportingDelay(parseInt(option.name))
                              }
                              options={reportingDelayOptions.map((option) => ({
                                name: option.name.toString(),
                                label: option.label,
                              }))}
                              placeholderLabel={t(
                                "createReport.selectReportingDelayTitle"
                              )}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <TimezoneSelect
                              handleSelect={setTimezone}
                              selectedTimezone={timezone}
                              tooltip={t("createReport.timezoneTooltip")}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="secondary">
                            {getScheduledDescription(
                              frequency,
                              t,
                              timezone,
                              reportingDelay
                            )}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {queryType && queryType.additionalFields && (
                      <Grid item xs={12}>
                        <AdditionalFieldsRenderer
                          reportType={queryType}
                          store={store}
                          onChange={handleAdditionalFieldsChange}
                          values={parameterValues}
                        />
                      </Grid>
                    )}

                    {queryType && queryType.hasCommentaryPrompt && (
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <AMCIncludeCommentaryToggle
                            includeCommentary={includeCommentary}
                            onToggle={() => {
                              setIncludeCommentary((prev) => !prev);
                            }}
                          />
                        </FormControl>
                      </Grid>
                    )}
                    {includeCommentary && user?.amcCustomPromptOn && (
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <Grid container alignItems="center">
                            <Typography
                              variant="subtitle1"
                              color="textSecondary"
                            >
                              {t("createReport.customPromptLabel")}
                            </Typography>
                          </Grid>
                          <TextField
                            fullWidth
                            placeholder={t(
                              "createReport.customPromptPlaceholder"
                            )}
                            value={customPrompt}
                            multiline
                            minRows={4}
                            onChange={(e) => {
                              if (e.target.value.length > 1000) {
                                setErrorForCustomPrompt(true);
                              } else {
                                setErrorForCustomPrompt(false);
                                setCustomPrompt(e.target.value);
                              }
                            }}
                          />
                          {errorForCustomPrompt && (
                            <ErrorMessage>
                              <Typography variant="body1" color="error">
                                {t("createReport.customPromptErorMessage")}
                              </Typography>
                            </ErrorMessage>
                          )}
                        </FormControl>
                      </Grid>
                    )}

                    {/* Hiding email recipients for now
                  <Grid item xs={12}>
                    <ReportEmailRecipients
                      emailToBeSent={emailsToBeSent}
                      setEmailToBeSent={setEmailsToBeSent}
                      emailError={emailError}
                      setEmailError={setEmailError}
                      fullWidth
                    />
                  </Grid> */}
                  </>
                )}
              </Grid>
            </Form>
          }
          footer={
            <>
              {error && (
                <Box width="100%" mb={2}>
                  <Typography variant="body2" color="error">
                    {t("amc.createScheduledReport.invalidParameterValues")}
                  </Typography>
                </Box>
              )}
              <Box width="100%">
                <AutoSizedButton
                  disabled={
                    !name || !queryType || isLoading || errorForCustomPrompt
                  }
                  type="submit"
                  variant="contained"
                  color="secondary"
                  onClick={handleSave}
                >
                  {isLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    t("createReport.generateButton")
                  )}
                </AutoSizedButton>
              </Box>
            </>
          }
        />
      </Box>
    </>
  );
};
